import { ReactNode, createContext, useContext } from 'react';
import { usePricingQuery } from '../../graphql/pricing.generated';
import { getLowestPrice } from './getLowestPrice';
import { getPricingInput } from './getPricingInput';
import type { Price, Product, ProductPrices } from '../../types';

interface ProductPricingContextProps {
    productPrices: ProductPrices | undefined;
    lowestPrice: Price | undefined;
    priceLoading: boolean;
}

const ProductPricingContext = createContext<ProductPricingContextProps | undefined>(undefined);

interface ProductPricingProviderProps {
    products: Product[];
    children: ReactNode;
}

export const ProductPricingProvider: React.FC<ProductPricingProviderProps> = ({
    products,
    children,
}) => {
    const pricingInput = getPricingInput(products);

    const { data, loading: priceLoading } = usePricingQuery({
        variables: {
            input: pricingInput,
        },
    });

    const productPrices = data?.pricing?.productPrices;
    const lowestPrice = getLowestPrice(productPrices);

    return (
        <ProductPricingContext.Provider value={{ productPrices, lowestPrice, priceLoading }}>
            {children}
        </ProductPricingContext.Provider>
    );
};

export const useProductPricingContext = () => {
    const context = useContext(ProductPricingContext);

    if (!context) {
        throw new Error('useProductPricingContext must be used within a ProductPricingProvider');
    }

    return context;
};
